import React, { useContext } from "react";
import { Form } from "react-final-form";
import { Loader2 } from "lucide-react";
import { AuthContext } from "context";
import { TextField } from "components/Form/Field";
import Button from "ui/Button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "components/ui/dialog";
import { STRIPE_CONNECTED_ACCOUNT_TOS_URL } from "config/externalLinks";
import { createStripeAccountToken } from "lib/stripe/createAccountToken";
import { createStripeAccount } from "hooks/profile";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { getErrorResponseData } from "helpers/typing";
import { isValidFrenchIban } from "helpers/cesu/bankValidation";
import { toast } from "sonner";

interface Values {
    account_name: string;
    iban: string;
}

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const CompleteStripeAccountDialog: React.FC<Props> = ({ open, onOpenChange }) => {
    const { account, updateAccount } = useContext(AuthContext);
    const provider = account?.resources?.provider?.[0];

    const onSubmit = async (values: Values) => {
        try {
            const publishableKey = process.env.REACT_APP_STRIPE_API_KEY_PUBLIC;
            if (!publishableKey) {
                throw new Error("Missing Stripe publishable key.");
            }
            if (!account?.user || !provider) {
                throw new Error("Missing provider profile.");
            }

            const stripeAccountToken = await createStripeAccountToken(publishableKey, {
                first_name: account.user.first_name,
                last_name: account.user.last_name,
                birthday: provider.birthday,
                phone: account.user.phone,
                house_number: account.user.house_number,
                street: account.user.street,
                address: account.user.address,
                city: account.user.city,
                postcode: account.user.postcode,
            });

            await createStripeAccount({
                stripe_account_token: stripeAccountToken,
                account_name: values.account_name,
                iban: values.iban,
            });
            await updateAccount();
            toast.success("Votre compte de paiement a bien été créé.");
            onOpenChange(false);
        } catch (err: unknown) {
            const errData = getErrorResponseData(err);
            if (errData) {
                return formatErrorResponse(errData);
            }
            toast.error("Impossible de créer le compte de paiement. Veuillez réessayer.");
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Finaliser vos coordonnées bancaires</DialogTitle>
                    <DialogDescription>
                        Votre inscription a été enregistrée, mais le compte de paiement n&apos;a pas pu être créé.
                        Confirmez votre RIB pour recevoir vos virements.
                    </DialogDescription>
                </DialogHeader>

                <Form
                    onSubmit={onSubmit}
                    initialValues={{
                        account_name: provider?.account_name ?? "",
                        iban: provider?.iban ?? "",
                    }}
                    validate={(values: Values) => {
                        const errors: Record<string, string> = {};
                        if (!String(values?.account_name ?? "").trim()) {
                            errors.account_name = "Le nom du titulaire est obligatoire.";
                        }
                        const iban = String(values?.iban ?? "");
                        if (!iban.trim()) {
                            errors.iban = "L'IBAN est obligatoire.";
                        } else if (!isValidFrenchIban(iban)) {
                            errors.iban = "IBAN français invalide.";
                        }
                        return errors;
                    }}
                    render={({ handleSubmit, submitting, values }) => (
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <TextField
                                inputProps={{
                                    name: "account_name",
                                    required: true,
                                    placeholder: "M. Jean Dupont",
                                    maxLength: 255,
                                }}
                                labelProps={{
                                    label: "Nom complet du titulaire du compte",
                                }}
                            />
                            <TextField
                                inputProps={{
                                    name: "iban",
                                    required: true,
                                    placeholder: "FR76 3000 6000 0112 3456 7890 123",
                                    maxLength: 34,
                                }}
                                labelProps={{
                                    label: "IBAN (présent sur votre RIB)",
                                }}
                            />
                            <p className="text-xs text-muted-foreground">
                                En validant, vous acceptez les{" "}
                                <a
                                    href={STRIPE_CONNECTED_ACCOUNT_TOS_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary"
                                >
                                    conditions d&apos;utilisation
                                </a>{" "}
                                des comptes connectés Stripe.
                            </p>
                            <Button
                                className="btn btn--primary w-full justify-center gap-2 rounded-full p-3"
                                type="submit"
                                disabled={
                                    submitting ||
                                    !String(values?.account_name ?? "").trim() ||
                                    !isValidFrenchIban(String(values?.iban ?? ""))
                                }
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                        Création du compte en cours...
                                    </>
                                ) : (
                                    "Valider et créer mon compte de paiement"
                                )}
                            </Button>
                        </form>
                    )}
                />
            </DialogContent>
        </Dialog>
    );
};

export default CompleteStripeAccountDialog;
